import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, canWrite } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { padrLabel } from "@/components/PadroncinoSelect";
import { stato, daysUntil, CANALI, AvvisaMenu, avvisaScadenza } from "@/pages/Scadenzario";
import { CalendarClock, ArrowRight } from "lucide-react";

const FILTRI = [
  { v: "15", label: "Entro 15 giorni", test: (n) => n >= 0 && n <= 15 },
  { v: "scadute", label: "Scadute", test: (n) => n < 0 },
  { v: "30", label: "Entro 30 giorni", test: (n) => n >= 0 && n <= 30 },
];
const TIPO_LBL = { assicurazione: "Assicurazione", revisione: "Revisione", tachigrafo: "Tachigrafo", patente: "Patente", carta_tachigrafica: "Carta tach.", altro: "Altro", documento: "Documento" };

export const ScadenzeRiepilogo = ({ company }) => {
  const { user } = useAuth();
  const writable = canWrite(user, "scadenze");
  const [items, setItems] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [padroncini, setPadroncini] = useState([]);
  const [filtro, setFiltro] = useState("15");
  const load = () => api.get("/scadenze", { params: { company } })
    .then((r) => setItems(r.data.sort((a, b) => a.data_scadenza.localeCompare(b.data_scadenza))));
  useEffect(() => {
    load();
    api.get("/veicoli", { params: { company } }).then((r) => setVeicoli(r.data));
    api.get("/padroncini", { params: { company } }).then((r) => setPadroncini(r.data));
  }, [company]); // eslint-disable-line

  const count = (f) => items.filter((s) => f.test(daysUntil(s.data_scadenza))).length;
  const shown = items.filter((s) => FILTRI.find((f) => f.v === filtro).test(daysUntil(s.data_scadenza)));

  return (
    <Card className="p-4 rounded-md" data-testid="dash-scadenze">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-heading font-bold flex items-center gap-2"><CalendarClock className="w-4 h-4" />Riepilogo scadenze</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTRI.map((f) => (
            <Button key={f.v} size="sm" variant={filtro === f.v ? "default" : "outline"} className="h-7 text-xs"
                    onClick={() => setFiltro(f.v)} data-testid={`dash-scad-filtro-${f.v}`}>{f.label} ({count(f)})</Button>
          ))}
          <Button asChild size="sm" variant="ghost" className="h-7 text-xs gap-1" data-testid="dash-scad-vai">
            <Link to="/scadenzario">Scadenzario<ArrowRight className="w-3.5 h-3.5" /></Link>
          </Button>
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="dash-scad-empty">Nessuna scadenza in questo periodo</div>
      ) : (
        <div className="overflow-x-auto max-h-80">
          <table className="autotrans-table w-full">
            <thead><tr><th>Tipo</th><th>Veicolo</th><th>Padroncino</th><th>Scadenza</th><th>Mancano</th><th>Broker</th><th>Ultimo avviso</th>{writable && <th></th>}</tr></thead>
            <tbody>
              {shown.map((s) => {
                const st = stato(s.data_scadenza);
                const p = padroncini.find((x) => x.id === s.padroncino_id);
                return (
                  <tr key={s.id} data-testid={`dash-scad-row-${s.id}`}>
                    <td><Badge variant="outline" className="h-5">{TIPO_LBL[s.tipo] || s.tipo}</Badge></td>
                    <td className="font-mono text-xs">{veicoli.find((v) => v.id === s.veicolo_id)?.targa || s.descrizione}</td>
                    <td className="text-xs">{padrLabel(p) || "—"}</td>
                    <td className="font-mono">{fmtDate(s.data_scadenza)}</td>
                    <td><Badge className={`${st.cls} text-white h-5 whitespace-nowrap`}>{st.txt}</Badge></td>
                    <td className="text-xs">{s.tipo === "assicurazione" ? (s.broker || "—") : ""}</td>
                    <td className="text-xs whitespace-nowrap">{s.ultimo_avviso
                      ? <span className="text-green-700 dark:text-green-400">{CANALI[s.ultimo_avviso.canale]} · {fmtDate(s.ultimo_avviso.data)}</span>
                      : <span className="text-orange-600">non avvisato</span>}</td>
                    {writable && <td><AvvisaMenu s={s} prefix="dash-scad" onAvvisa={(x, c) => avvisaScadenza(x, c, load)} /></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
